import { DonationFormType } from "../cms/modernBlocks/blocks/donationFormBlock/donationFormBlock.state"
import { sitename } from "../globals"
import { donationDataLayerServices } from "./datalayer.donation"

export type WindowWithDatalayer = {
  dataLayer: {
    push: (args: datalayerObject) => void
  }
}

export type datalayerObject = {
  event?: string
  category?: string
  action?: string
  label?: string
  click_url?: string
  click_text?: string
  articleNumber?: string
  gender?: string
  progress?: string
  flow?: string
  amount?: string
  "email_opt-in"?: string
  "telefoon_opt-in"?: string
} | GtmBeginCheckoutEvent

export type scheduledDatalayerObject = {
  event?: string
  label?: string
} | GtmPurchaseEvent

export type frequency = 'once' | 'monthly' | 'yearly'
export type donationVariant = 'IDEAL' | 'IBAN'

const cookiesDatalayer = (accepted: boolean) =>
  pushDatalayer({
    event: 'cookies',
    category: 'cookie consent',
    action: accepted ? 'true' : 'false',
    label: accepted ? '1000' : '-100'
  })

const downloadDataLayer = (kind: 'shop' | 'download', label: string, articleNumber?: string) =>
  pushDatalayer({
    event: kind == 'download' ? 'download' : 'download_shop',
    category: kind == 'download' ? 'file_download' : 'downloads',
    action: label.slice(label.lastIndexOf(".") + 1),
    label: label,
    articleNumber: kind == 'download' ? '' : articleNumber ? articleNumber : ''
  })

const newsletterDataLayer = (opt: boolean, label: string, gender?: string) =>
  pushDatalayer({
    event: 'newsletter',
    category: 'newsletter',
    action: opt ? 'opt-in' : 'subscription',
    label: label,
    gender: gender ? (gender == 'vrouw' ? 'F' : 'M') : 'N/A'
  })

const videoDataLayer = (label: string, type: 'video_start' | 'video_progress' | 'video_interactions' | 'video_complete', progress?: number) =>
  pushDatalayer({
    event: 'video_interactions',
    category: 'video',
    action: type,
    label: label,
    progress: progress ? (progress == 25 || progress == 50 || progress || 75 || progress == 90) ? `${progress.toString()}%` : undefined : undefined
  })

const tagsDataLayer = (action: string) => (
  pushDatalayer({
    event: 'tags',
    category: 'tags',
    action: action,
    label: ''
  })
)

const flyoutDataLayer = (action: string) => (
  pushDatalayer({
    event: 'flyout',
    category: 'fly_out',
    action: action,
    label: 'open'
  })
)

const hartleftijdDataLayer = (action: string, label: string) => (
  pushDatalayer({
    event: 'hartleftijd',
    category: 'hartleftijd',
    action: action,
    label: label
  })
)

const buttonLinkClickDataLayer = (click_url: string, click_text: string) => (
  pushDatalayer({
    event: 'button_link_click',
    click_url: click_url,
    click_text: click_text,
  })
)

type GtmPurchaseEvent = {
  event: 'purchase'
  currency: string
  transaction_id: string
  value: string
  donationFrequency: frequency
  items: {
    item_name: string
    item_category: frequency
    item_variant: donationVariant
    price: string
    quantity: number
  }[]
}

type GtmBeginCheckoutEvent = {
  event: 'begin_checkout'
  currency: string
  value: string
  donationFrequency: frequency
  items: {
    item_name: string
    item_category: frequency
    item_variant: donationVariant | ""
    price: string
    quantity: number
  }[]
}

const hamburgerMenuDataLayer = (action: 'open' | 'close') => (
  pushDatalayer({
    event: 'menu',
    category: 'menu',
    action: action,
    label: ''
  })
)

const menuItemDataLayer = (action: string, label: string) => (
  pushDatalayer({
    event: 'menu',
    category: 'menu',
    action: action,
    label: label
  })
)

const beroerteDataLayer = (action: 'Button klik' | 'Antwoord klik' | 'Resultaat getoond' | 'Openklapper' | 'Dichtklapper', label: string) => (
  pushDatalayer({
    event: 'beroertetest',
    category: `Beroertetest: variant video's met situatieschets in tekst`,
    action: action,
    label: label
  })
)

const bloeddrukquizDataLayer = (action: 'Button klik' | 'Antwoord klik' | 'Resultaat getoond' | 'Openklapper' | 'Dichtklapper', label: string) => (
  pushDatalayer({
    event: 'bloeddrukquiz',
    category: 'Test je kennis over bloeddruk',
    action: action,
    label: label
  })
)

const postcodeDataLayer = (action: string, label: string) => (
  pushDatalayer({
    event: 'postcodechecker',
    category: 'Postcodechecker',
    action: action,
    label: label
  })
)

const expandableDataLayer = (action: string) => (
  pushDatalayer({
    event: 'flyout',
    category: 'fly_out',
    action: action,
    label: "open"
  })
)

export const pushDatalayer = (d: datalayerObject) => {
  const defaultParams: Record<string, string> = {
    event: `event.${sitename}`,
    label: window.location.pathname
  }

  const skipLabelEvents = ['purchase', 'begin_checkout']
  if (typeof d.event === 'string' && skipLabelEvents.includes(d.event)) delete defaultParams.label

  const params = { ...defaultParams, ...d }
  window.dataLayer ? window.dataLayer.push(params) : console.log(params)
}

export const dataLayerServices = {
  ...donationDataLayerServices,
  cookiesDatalayer,
  downloadDataLayer,
  newsletterDataLayer,
  videoDataLayer,
  buttonLinkClickDataLayer,
  hamburgerMenuDataLayer,
  menuItemDataLayer,
  beroerteDataLayer,
  bloeddrukquizDataLayer,
  postcodeDataLayer,
  tagsDataLayer,
  flyoutDataLayer,
  hartleftijdDataLayer,
  expandableDataLayer,
}
